import styled from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  brandPrimary90,
  darkModeTextColor,
  lightNeutral70,
  lightTextLink,
} from "constants/colors";
import { useIsDarkMode } from "store/atoms";
import {
  INITIAL_PROCESS_TILES_FILTER_STATE,
  useProcessTilesFilterState,
} from "./useProcessTilesFilterState";
import { Button, Chip } from "@mui/material";
import { CATEGORY_TO_LABEL_MAP, PNL_COLORS } from "constants/constants";
import { isEqual } from "lodash";
import {
  ArrowDownward,
  ArrowUpward,
  Clear,
  PrecisionManufacturing,
  Search,
  Category,
  Sell,
  FilterList,
} from "@mui/icons-material";
import { CUSTOM_SCROLLBAR_CSS_HORIZONTAL } from "App/CUSTOM_SCROLLBAR_CSS";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import { startTransition } from "react";
import {
  startProcessTilesPanelPending,
  stopProcessTilesPanelPending,
} from "./ProcessTilesFilterControls";

/** Filter indicators for process tiles, like "status", "sort by duration", "search for a process" */
export function ProcessTilesFilterIndicator() {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  const visibleProcesses = useVisibleProcesses();
  const noFilters =
    isEqual(filterState, INITIAL_PROCESS_TILES_FILTER_STATE) ||
    isEqual(
      {
        ...filterState,
        selectedProcessNames: filterState.selectedProcessNames.length,
      },
      {
        ...INITIAL_PROCESS_TILES_FILTER_STATE,
        selectedProcessNames: visibleProcesses.length,
      }
    );
  return (
    <ProcessTilesFilterIndicatorStyles $isDarkMode={isDarkMode}>
      <div className="filtersLabel">{translate("Filters:")}</div>
      {/* one Chip for each pnl category in filterState */}
      {noFilters ? (
        <div className="noFilters">{translate("None")}</div>
      ) : (
        <div className="filtersRow">
          {filterState.searchText ? (
            <Chip
              variant="outlined"
              label={
                <>
                  <div
                    style={{
                      marginLeft: -6,
                      transform: "scale(0.7)",
                    }}
                  >
                    <Search />
                  </div>{" "}
                  {translate("Name:")} {filterState.searchText}
                </>
              }
              onDelete={() => {
                setFilterState(prev => ({ ...prev, searchTextDisplay: "" }));
                startProcessTilesPanelPending();
                startTransition(() => {
                  stopProcessTilesPanelPending();
                  setFilterState(prev => ({ ...prev, searchText: null }));
                });
              }}
              deleteIcon={<Clear />}
            />
          ) : null}
          {filterState.sortBy ? (
            <Chip
              variant="outlined"
              label={
                <>
                  <div
                    style={{
                      marginLeft: -8,
                      transform: "translate(0, -1px) scale(0.7)",
                    }}
                  >
                    {filterState.sortBy.order === "desc" ? (
                      <ArrowDownward style={{ marginBottom: -1 }} />
                    ) : (
                      <ArrowUpward />
                    )}
                  </div>{" "}
                  {translate(filterState.sortBy.label)}
                </>
              }
              onDelete={() => {
                const newFilterState = {
                  ...filterState,
                  sortBy: null,
                };
                startProcessTilesPanelPending();
                startTransition(() => {
                  stopProcessTilesPanelPending();
                  setFilterState(newFilterState);
                });
              }}
              deleteIcon={<Clear />}
            />
          ) : null}
          {filterState.groupBy ? (
            <Chip
              variant="outlined"
              label={
                <>
                  <div
                    style={{
                      marginLeft: -8,
                      transform: "translate(0, -1px) scale(0.7)",
                    }}
                  >
                    <Category />
                  </div>{" "}
                  {translate(filterState.groupBy.label)}
                </>
              }
              onDelete={() => {
                const newFilterState = {
                  ...filterState,
                  groupBy: null,
                };
                startProcessTilesPanelPending();
                startTransition(() => {
                  stopProcessTilesPanelPending();
                  setFilterState(newFilterState);
                });
              }}
              deleteIcon={<Clear />}
            />
          ) : null}
          {/* Top Losses Filter Chip */}
          {filterState.filterByTopLosses &&
            filterState.filterByTopLosses.length > 0 &&
            !filterState.filterByTopLosses.includes("SELECT_ALL") && (
              <Chip
                variant="outlined"
                label={
                  <>
                    <div
                      style={{
                        marginLeft: -8,
                        transform: "translate(0, -1px) scale(0.7)",
                      }}
                    >
                      <FilterList />
                    </div>{" "}
                    {translate("Filtered Top Losses")}
                  </>
                }
                onDelete={() => {
                  const newFilterState = {
                    ...filterState,
                    filterByTopLosses: [],
                  };
                  startProcessTilesPanelPending();
                  startTransition(() => {
                    stopProcessTilesPanelPending();
                    setFilterState(newFilterState);
                  });
                }}
                deleteIcon={<Clear />}
              />
            )}

          {filterState.filterByTagNames &&
            filterState.filterByTagNames.length > 0 &&
            filterState.filterByTagNames.map(tag => (
              <Chip
                key={tag}
                variant="outlined"
                label={
                  <>
                    <div
                      style={{
                        marginLeft: -8,
                        transform: "translate(0, -1px) scale(0.7)",
                      }}
                    >
                      <Sell />
                    </div>{" "}
                    {translate(tag)}
                  </>
                }
                onDelete={() => {
                  const newFilterState = {
                    ...filterState,
                    filterByTagNames:
                      filterState.filterByTagNames?.filter(
                        tagName => tagName !== tag
                      ) ?? null,
                  };
                  startProcessTilesPanelPending();
                  startTransition(() => {
                    stopProcessTilesPanelPending();
                    setFilterState(newFilterState);
                  });
                }}
                deleteIcon={<Clear />}
              />
            ))}
          {filterState.filterByCategory.includes("SELECT_ALL")
            ? null
            : filterState.filterByCategory.map(category => {
                return (
                  <Chip
                    key={category}
                    variant="outlined"
                    label={
                      <>
                        <div
                          className="category-circle"
                          style={{ background: PNL_COLORS[category] }}
                        ></div>
                        {CATEGORY_TO_LABEL_MAP[category]}
                      </>
                    }
                    onDelete={() => {
                      const newFilterState = {
                        ...filterState,
                        filterByCategory: filterState.filterByCategory.filter(
                          c => c !== category
                        ),
                      };
                      startProcessTilesPanelPending();
                      startTransition(() => {
                        stopProcessTilesPanelPending();
                        setFilterState(newFilterState);
                      });
                    }}
                    deleteIcon={<Clear />}
                  />
                );
              })}
          {filterState.selectedProcessNames.length > 0 &&
          filterState.selectedProcessNames.length !== visibleProcesses.length
            ? filterState.selectedProcessNames
                .filter(name => name !== "SELECT_ALL")
                .slice(0, 12)
                .map(processName => (
                  <Chip
                    key={processName}
                    variant="outlined"
                    label={
                      <>
                        <div
                          style={{
                            marginLeft: -8,
                            transform: "translate(0, -1px) scale(0.7)",
                          }}
                        >
                          <PrecisionManufacturing />
                        </div>{" "}
                        {processName}
                      </>
                    }
                    onDelete={() => {
                      const newSelectedProcessNames =
                        filterState.selectedProcessNames.filter(
                          p => p !== processName
                        );
                      startProcessTilesPanelPending();
                      startTransition(() => {
                        stopProcessTilesPanelPending();
                        setFilterState(prev => ({
                          ...prev,
                          selectedProcessNames:
                            newSelectedProcessNames.length === 0
                              ? visibleProcesses.map(p => p.name)
                              : newSelectedProcessNames,
                        }));
                      });
                    }}
                    deleteIcon={<Clear />}
                  />
                ))
            : null}
          {filterState.selectedProcessNames.length !==
            visibleProcesses.length &&
            filterState.selectedProcessNames.length > 12 && (
              <Chip
                variant="outlined"
                label={<>{translate("and more...")}</>}
              />
            )}
          <Button
            className={"btnClearAllFilters doNotPrint"}
            variant="text"
            onClick={() => {
              startProcessTilesPanelPending();
              setFilterState(prev => ({ ...prev, searchTextDisplay: "" }));
              startTransition(() => {
                stopProcessTilesPanelPending();
                setFilterState({
                  ...INITIAL_PROCESS_TILES_FILTER_STATE,
                  selectedProcessNames: visibleProcesses.map(p => p.name),
                });
              });
            }}
          >
            {translate("Clear All")}
          </Button>
        </div>
      )}
    </ProcessTilesFilterIndicatorStyles>
  );
}

const ProcessTilesFilterIndicatorStyles = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  overflow: hidden;
  height: 32px;
  padding-bottom: 12px;
  .filtersLabel {
    margin-right: 26px;
  }
  .noFilters {
    font-weight: bold;
    color: ${lightTextLink};
  }
  .filtersRow {
    display: flex;
    align-items: center;
    ${CUSTOM_SCROLLBAR_CSS_HORIZONTAL}

    /* when hz scrollbar is visible (on small screens with many filter chips), chips get pushed up & tops get cut off */
    margin-bottom: -18px;
    overflow-x: scroll !important;
    &::-webkit-scrollbar {
      height: 16px;
    }
    &::-webkit-scrollbar-thumb {
      border: 6px solid transparent;
    }
  }
  && .btnClearAllFilters {
    white-space: nowrap;
    border: none;
    color: ${lightTextLink};
    font-weight: bold;
    line-height: 17px;
  }
  .MuiButtonBase-root {
    color: ${p => (p.$isDarkMode ? darkModeTextColor : "black")};
    text-transform: none;
    border: 1px solid
      ${p => (p.$isDarkMode ? darkModeTextColor : lightNeutral70)};
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 14px;
    line-height: 0px;
  }

  .MuiChip-root {
    padding: 10px 4px 10px 10px;
    color: ${brandPrimary90};
    margin-right: 18px;
    border-color: ${brandPrimary90};
  }
  .MuiChip-label {
    overflow: visible;
    display: flex;
    align-items: center;
    padding: 0 16px 0 0;
  }
  .MuiChip-deleteIcon {
    margin-right: 0;
    color: ${p => (p.$isDarkMode ? darkModeTextColor : brandPrimary90)};
    width: 20px;
    height: 20px;
  }
  .category-circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
  }
`;
